// src/components/ui/DbMigrationGuard.tsx
//
// Detecta si la base de datos local fue migrada automÃ¡ticamente al abrir
// la aplicaciÃ³n (esquema obsoleto) y presenta un aviso claro al usuario
// con la opciÃ³n de confirmar y continuar sin perder datos.

import { useState, useEffect } from 'react';
import { Database, CheckCircle2, RefreshCw, ShieldCheck, ArrowRight } from 'lucide-react';

// Clave en sessionStorage para no mostrar el aviso mÃ¡s de una vez por sesiÃ³n
const SESSION_KEY = 'niiz_db_migration_ack';
// Clave en localStorage para guardar la Ãºltima versiÃ³n vista por el usuario
const STORAGE_KEY = 'niiz_db_last_version';
// VersiÃ³n actual del esquema (debe coincidir con el Ãºltimo .version() en db.ts)
const CURRENT_DB_VERSION = 107;

export const DbMigrationGuard = () => {
  const [visible, setVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [migrationInfo, setMigrationInfo] = useState<{
    fromVersion: number;
    toVersion: number;
  } | null>(null);

  useEffect(() => {
    // Si ya se confirmÃ³ en esta sesiÃ³n, no mostrar de nuevo
    if (sessionStorage.getItem(SESSION_KEY)) return;

    const lastVersion = Number(localStorage.getItem(STORAGE_KEY) || '0');

    // Si el usuario ya estaba en la versiÃ³n actual, nada que mostrar
    if (lastVersion === CURRENT_DB_VERSION) return;

    // Si es una instalaciÃ³n nueva (lastVersion === 0), solo guardar la versiÃ³n
    // sin mostrar el aviso (no hay migraciÃ³n, es primera vez)
    if (lastVersion === 0) {
      localStorage.setItem(STORAGE_KEY, String(CURRENT_DB_VERSION));
      sessionStorage.setItem(SESSION_KEY, '1');
      return;
    }

    // Hay una diferencia de versiones: la DB fue o serÃ¡ migrada
    setMigrationInfo({ fromVersion: lastVersion, toVersion: CURRENT_DB_VERSION });
    setVisible(true);
  }, []);

  const handleAccept = async () => {
    setIsProcessing(true);

    try {
      // PequeÃ±a pausa para dar feedback visual
      await new Promise(resolve => setTimeout(resolve, 600));

      // Guardar la nueva versiÃ³n como "vista"
      localStorage.setItem(STORAGE_KEY, String(CURRENT_DB_VERSION));
      sessionStorage.setItem(SESSION_KEY, '1');

      setIsProcessing(false);
      setVisible(false);
    } catch {
      setIsProcessing(false);
      setVisible(false);
    }
  };

  if (!visible || !migrationInfo) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300">
      <div className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in zoom-in-95 duration-300">

        {/* Header con gradiente */}
        <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-700 p-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,_white_0%,_transparent_60%)]" />
          <div className="relative flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shrink-0 ring-2 ring-white/30">
              <Database size={28} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-black leading-tight tracking-tight">
                ActualizaciÃ³n de Base de Datos
              </h2>
              <p className="text-indigo-200 text-sm mt-0.5 font-medium">
                Tu base de datos local ha sido actualizada
              </p>
            </div>
          </div>
        </div>

        {/* Cuerpo */}
        <div className="p-6 space-y-5">

          {/* Indicador de versiÃ³n */}
          <div className="flex items-center justify-center gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="text-center">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">VersiÃ³n anterior</p>
              <p className="text-2xl font-black text-slate-500 dark:text-slate-400">v{migrationInfo.fromVersion}</p>
            </div>
            <div className="flex-1 flex items-center justify-center px-2">
              <div className="w-full border-t-2 border-dashed border-indigo-200 dark:border-indigo-800 relative flex justify-center">
                <ArrowRight size={14} className="text-indigo-500 bg-slate-50 dark:bg-slate-800 px-1 absolute -top-[7px]" />
              </div>
            </div>
            <div className="text-center">
              <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">Nueva versiÃ³n</p>
              <p className="text-2xl font-black text-indigo-600 dark:text-indigo-400">v{migrationInfo.toVersion}</p>
            </div>
          </div>

          {/* DescripciÃ³n */}
          <div className="space-y-3">
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              NIIZ actualizÃ³ automÃ¡ticamente la estructura de tu base de datos local para
              compatibilidad con las nuevas funciones.
            </p>
            <div className="space-y-2">
              {[
                'Todos tus datos fueron preservados sin cambios',
                'Productos, ventas y clientes estÃ¡n intactos',
                'Nuevas funciones ya estÃ¡n disponibles',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm text-slate-600 dark:text-slate-300">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                    <CheckCircle2 size={12} strokeWidth={3} />
                  </div>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Nota tÃ©cnica */}
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
            <p className="text-[11px] text-amber-700 dark:text-amber-400 font-medium leading-snug">
              <strong>Nota:</strong> La migraciÃ³n se ejecuta automÃ¡ticamente al inicio.
              Si experimentas algÃºn problema, ve a ConfiguraciÃ³n â†’ Sistema â†’ Reparar Datos.
            </p>
          </div>
        </div>

        {/* Footer con botÃ³n */}
        <div className="px-6 pb-6">
          <button
            id="db-migration-accept-btn"
            onClick={handleAccept}
            disabled={isProcessing}
            className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-black text-sm rounded-xl shadow-lg shadow-indigo-500/30 flex items-center justify-center gap-3 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <>
                <RefreshCw size={18} className="animate-spin" />
                Aplicando migraciÃ³n...
              </>
            ) : (
              <>
                <ShieldCheck size={18} />
                Entendido, continuar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
